import { Container, Spinner, XStack, YStack } from '@my/ui'
import React from 'react'
import { MainLayout, Meta } from 'app/components'
import {
  CreatePostButton,
  GroupPostCard,
  GroupNoResults,
  GroupEventCard,
  GroupAnnouncementCard,
} from '../../components'
import { User } from 'app/types'
import { useInView } from 'react-intersection-observer'
import { canInteract } from '../../helpers'
import { GroupAdBlockRenderer } from 'app/features/ads/blocks'
import { GroupPostOrAd } from './groups-feed-screen.container'

type GroupsFeedScreenProps = {
  HeaderComponent?: React.ReactNode
  feed?: GroupPostOrAd[]
  feedLoading?: boolean
  feedFetching?: boolean
  feedFetchingMore?: boolean
  refetchFeed: () => void
  hasMore?: boolean
  onCreatePostOpenChange: (open: boolean) => void
  fetchMore?: () => void
  user: Pick<User, 'id' | 'userType' | 'fullName' | 'avatar' | 'universityEmailVerified'> | null
}

export function GroupsFeedScreen(props: GroupsFeedScreenProps) {
  const { feed, user } = props

  const [fetchMoreElRef] = useInView({
    onChange: (inView) => {
      if (!props.hasMore || !inView) {
        return
      }
      props.fetchMore && props.fetchMore()
    },
    threshold: 0,
    delay: 50,
    rootMargin: '0px 0px 40% 0px',
  })

  return (
    <MainLayout>
      <Meta
        title="Uni Chat & Groups"
        description="Chat Groups with Friends, Coursemates, Societies etc."
      />
      {props.HeaderComponent && props.HeaderComponent}
      <Container size="sm" spaceTop spaceBottom gap="$3.5">
        {user && (
          <Container size="sm" gutter>
            <CreatePostButton user={user} onPress={() => props.onCreatePostOpenChange(true)} />
          </Container>
        )}

        {!props.feedLoading && !props.feedFetching && feed?.length === 0 && (
          <Container size="sm" gutter>
            <GroupNoResults
              title="Sorry, there’s no posts available at the moment"
              description="But don’t worry, refresh and try again or join a group to get your feed and running!"
            />
          </Container>
        )}

        {props.user && feed && feed?.length > 0 && (
          <YStack gap="$3.5">
            {feed.map((item, i) => {
              if (item.type === 'ad') {
                return (
                  <Container key={`Ad:${i}`} size="sm" gutter="gtXs">
                    <GroupAdBlockRenderer {...item.data} />
                  </Container>
                )
              }

              if (item.type === 'groupPost') {
                const post = item.data
                return (
                  <Container key={post.id} size="sm" gutter="gtXs">
                    <GroupPostCard
                      displayAs="feedItem"
                      user={props.user}
                      canInteract={canInteract({ user: props.user, group: post.group })}
                      canExpandContent={true}
                      onAfterDelete={props.refetchFeed}
                      post={post}
                    />
                  </Container>
                )
              }

              if (item.type === 'groupEvent') {
                const event = item.data
                return (
                  <Container key={event.id} size="sm" gutter>
                    <GroupEventCard
                      displayAs="feedItem"
                      user={props.user}
                      canInteract={canInteract({ user: props.user, group: event.group })}
                      event={event}
                      onAfterDelete={props.refetchFeed}
                    />
                  </Container>
                )
              }

              if (item.type === 'groupAnnouncement') {
                const announcement = item.data
                return (
                  <Container key={announcement.id} size="sm" gutter="gtXs">
                    <GroupAnnouncementCard
                      displayAs="feedItem"
                      user={props.user}
                      canInteract={canInteract({ user: props.user, group: announcement.group })}
                      canExpandContent={true}
                      onAfterDelete={props.refetchFeed}
                      announcement={announcement}
                    />
                  </Container>
                )
              }

              return null
            })}
          </YStack>
        )}

        {(props.feedLoading || props.feedFetching || props.feedFetchingMore) && (
          <XStack w="100%" jc="center" ai="center" h={100}>
            <Spinner color="$black" />
          </XStack>
        )}

        <XStack w="100%" opacity={0} ref={fetchMoreElRef as any} />
      </Container>
    </MainLayout>
  )
}
